import { Box, Divider, List, Paper, Typography } from "@mui/material";
import { useEffect, useRef, useState } from "react";
import { map, orderBy as orderByLodash } from "lodash";
import { radius } from "../../../../theme";
import { useUsuario } from "../../../../providers/usuarioProvider";
import UploadImportado from "./uploadImportado";
import UploadImportando from "./uploadImportando";
import UploadComErro from "./uploadComErro";
import NotasManuais from "./notasManuais";
import { SituacaoUpload } from "../../../../models/enum";
import { limit, onSnapshot, orderBy, query, where } from "firebase/firestore";
import UploadDb from "../../../../db/uploadDb";
import NotaDb from "../../../../db/notaDb";
import Upload from "../../../../models/upload";
import { NotaBMF, NotaBovespa } from "../../../../models/nota";

export default function ListaUploads() {
  const { visualizado } = useUsuario();
  const [uploads, setUploads] = useState<Upload[]>([]);
  const [uploadsImportados, setUploadsImportados] = useState<Upload[]>([]);
  const [notasManuais, setNotasManuais] = useState<(NotaBovespa | NotaBMF)[]>(
    []
  );
  const [hasMore, setHasMore] = useState(true); // Para saber se há mais itens a carregar
  const loaderRef = useRef<HTMLDivElement | null>(null); // Referência ao componente de carregamento
  const [limitItens, setLimit] = useState(20);

  useEffect(() => {
    if (!!visualizado)
      return onSnapshot(
        query(
          new UploadDb().collection({ usuarioId: visualizado, uploadId: "" }),
          where("situacao", "!=", SituacaoUpload.IMPORTADO)
        ),
        (snapshot) => setUploads(map(snapshot.docs, (doc) => doc.data()))
      );
  }, [visualizado]);

  useEffect(() => {
    if (!!visualizado)
      return onSnapshot(
        query(
          new UploadDb().collection({ usuarioId: visualizado, uploadId: "" }),
          where("situacao", "==", SituacaoUpload.IMPORTADO),
          orderBy("periodo.de", "desc"),
          limit(limitItens)
        ),
        (snapshot) => {
          const uploadsCarregados = map(snapshot.docs, (doc) => doc.data());
          if (uploadsCarregados.length === uploadsImportados.length) {
            setHasMore(false);
          }
          setUploadsImportados(uploadsCarregados);
        }
      );
  }, [visualizado, limitItens]);

  const carregarMaisItens = () => {
    if (!hasMore || !!!visualizado) return;
    setLimit((prev) => prev + 20);
  };

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore) {
          carregarMaisItens(); // Carrega mais itens quando o loader estiver visível
        }
      },
      { threshold: 1.0 } // Dispara quando o elemento está 100% visível
    );

    if (loaderRef.current) {
      observer.observe(loaderRef.current);
    }

    return () => {
      if (loaderRef.current) {
        observer.unobserve(loaderRef.current);
      }
    };
  }, [hasMore, uploadsImportados]);

  useEffect(() => {
    if (!!visualizado)
      return onSnapshot(
        query(
          new NotaDb().collection({ usuarioId: visualizado, notaId: "" }),
          where("numeroNota", "!=", "")
        ),
        (snapshot) => {
          if (!snapshot.empty) {
            const notasManuaisSnapshot = orderByLodash(
              map(snapshot.docs, (doc) => doc.data()),
              (nota) => nota.dataPregao
            );
            setNotasManuais(notasManuaisSnapshot);
          }
        }
      );
  }, [visualizado]);

  return (
    <Paper
      variant="outlined"
      sx={{ borderRadius: `${radius.xl}px`, overflow: "hidden" }}
    >
      <List sx={{ py: 0 }}>
        {notasManuais.length > 0 && (
          <>
            <NotasManuais notas={notasManuais} />
            <Divider variant="fullWidth" />
          </>
        )}
        {map(
          orderByLodash(
            [...uploads, ...uploadsImportados],
            [
              (u) => {
                switch (u.situacao) {
                  case SituacaoUpload.IMPORTADO:
                    return 2;
                  case SituacaoUpload.ERRO_AO_IMPORTAR:
                  case SituacaoUpload.ERRO_AO_LER:
                    return 1;
                  case SituacaoUpload.PENDENTE:
                  case SituacaoUpload.SALVO:
                  default:
                    return 0;
                }
              },
              (u) => (!!u.incompatibilidades || !!u.quantidadeParciais ? 0 : 1),
              (u) => u.periodo?.de.unix() ?? 0,
            ],
            ["asc", "asc", "desc"]
          ),
          (upload, index) => {
            let componente;
            switch (upload.situacao) {
              case SituacaoUpload.IMPORTADO:
                componente = (
                  <UploadImportado key={upload.id} upload={upload} />
                );
                break;
              case SituacaoUpload.SALVO:
              case SituacaoUpload.PENDENTE:
                componente = (
                  <UploadImportando key={upload.id} upload={upload} />
                );
                break;
              case SituacaoUpload.ERRO_AO_IMPORTAR:
              case SituacaoUpload.ERRO_AO_LER:
              default:
                componente = <UploadComErro key={upload.id} upload={upload} />;
                break;
            }
            return (
              <>
                {componente}
                {index < uploads.length - 1 && <Divider variant="fullWidth" />}
              </>
            );
          }
        )}
      </List>
      {hasMore && (
        <Box
          display="flex"
          flexDirection="row"
          justifyContent="center"
          width="100%"
          paddingY={2}
        >
          <div ref={loaderRef}>
            <Typography variant="labelMono" color="text.secondary">
              Carregando mais itens...
            </Typography>
          </div>
        </Box>
      )}
    </Paper>
  );
}
